import { ExternalLinkIcon } from 'lucide-react'

import { TextButton } from 'slab'
import { cn } from 'utils'
import { type Money } from 'utils/web_api.schemas'

import ChangeIndicator from '../ChangeIndicator/ChangeIndicator'

export interface RowValues {
  label: string;
  prevValue?: string | React.ReactNode;
  newValue?: string | React.ReactNode;
  yoyChange?: Money;
  increaseIsGreen: boolean;
  compactRow?: boolean;
}

interface RenewalsPlanComparisonTableProps {
  prevPlanYear: number;
  currPlanYear: number;
  onNavigateToPrevPlanDetailsPage?: () => void;
  onNavigateToCurrPlanDetailsPage?: () => void;
  rows: RowValues[];
  thereIsNoNextPlanMessage?: string;
}

const CELL_STYLES = 'p-2 xs:p-4 body-sm xs:body'
const ROW_STYLES = 'border-b border-gray-200'

const RenewalsPlanComparisonTable: React.FC<
  RenewalsPlanComparisonTableProps
> = ({
  prevPlanYear,
  currPlanYear,
  onNavigateToPrevPlanDetailsPage,
  onNavigateToCurrPlanDetailsPage,
  rows,
  thereIsNoNextPlanMessage,
}) => {
  let noNextPlanMessageRowSpan: number | undefined
  if (thereIsNoNextPlanMessage) {
    noNextPlanMessageRowSpan = rows.length
    if (onNavigateToPrevPlanDetailsPage || onNavigateToCurrPlanDetailsPage) {
      noNextPlanMessageRowSpan += 1
    }
  }

  return (
    <table className='table-fixed w-full'>
      {/* Year headers */}
      <thead>
        <tr className={cn(ROW_STYLES)}>
          <th className={cn(CELL_STYLES)} />
          <th className={cn(CELL_STYLES, 'w-1/3 text-left text-gray-500')}>
            {prevPlanYear}
          </th>
          <th
            className={cn(
              CELL_STYLES,
              'w-1/3 text-left bg-gray-50 body-bold text-thatch-500'
            )}
          >
            {currPlanYear}
          </th>
        </tr>
      </thead>
      {/* shows plan premiums and other plan feature comparisons */}
      <tbody>
        {rows.map((row, index) => {
          return (
            <tr className={cn(ROW_STYLES)} key={row.label}>
              <td className={cn(CELL_STYLES, 'text-gray-500')}>{row.label}</td>
              <td className={cn(CELL_STYLES, 'text-gray-800', { 'py-2': row.compactRow })}>
                {row.prevValue}
              </td>
              {index === 0 && !!noNextPlanMessageRowSpan && (
                <td
                  className={cn(CELL_STYLES, 'bg-gray-50 text-gray-500 text-center')}
                  rowSpan={noNextPlanMessageRowSpan}
                >
                  {thereIsNoNextPlanMessage}
                </td>
              )}
              {!noNextPlanMessageRowSpan && (
                <td className={cn(CELL_STYLES, 'bg-gray-50 text-gray-800', { 'py-2': row.compactRow })}>
                  {(row.newValue || row.yoyChange) && (
                    <div className='flex flex-col xs:flex-row items-center justify-between'>
                      <span>{row.newValue}</span>
                      {row.yoyChange && (
                        <span>
                          <ChangeIndicator
                            change={row.yoyChange?.subunits}
                            displayValue={
                              row.yoyChange?.subunits === 0
                                ? undefined
                                : row.yoyChange?.formatted_amount
                            }
                            increaseIsGreen={row.increaseIsGreen}
                            size='small'
                            makeInvisible={false}
                          />
                        </span>
                      )}
                    </div>
                  )}
                </td>
              )}
            </tr>
          )
        })}
        {/* View full plan details buttons - will link to medical plan details page or dental/vision SBC document */}
        {(onNavigateToPrevPlanDetailsPage ||
          onNavigateToCurrPlanDetailsPage) && (
            <tr>
              <td className={cn(CELL_STYLES)} />
              <td className={cn(CELL_STYLES)}>
                {onNavigateToPrevPlanDetailsPage && (
                  <TextButton
                    onClick={onNavigateToPrevPlanDetailsPage}
                    type='button'
                    rightIcon={ExternalLinkIcon}
                  >
                    View full plan details
                  </TextButton>
                )}
              </td>
              {!noNextPlanMessageRowSpan && (
                <td className={cn(CELL_STYLES, 'bg-gray-50')}>
                  {onNavigateToCurrPlanDetailsPage && (
                    <TextButton
                      onClick={onNavigateToCurrPlanDetailsPage}
                      type='button'
                      rightIcon={ExternalLinkIcon}
                    >
                      View full plan details
                    </TextButton>
                  )}
                </td>
              )}
            </tr>
        )}
      </tbody>
    </table>
  )
}

export default RenewalsPlanComparisonTable
